import { ChangeEventHandler, KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { PaginationNumberFieldProps } from "./PaginationNumberField.types";

const usePresenter = (props: PaginationNumberFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(`${props.page}`);

  useEffect(() => {
    setPage(`${props.page}`);
  }, [props.page]);

  const onPageChanged: ChangeEventHandler<HTMLInputElement> = (event) => {
    // Update only local state while typing
    // This prevents triggering pagination updates on every keystroke
    setPage(event.target.value);
  };

  const commitPage = () => {
    // Commit the page change only when the user finishes input
    const parsed = parseInt(page, 10);

    if (isNaN(parsed)) {
      // Revert to the current page if the input is invalid
      setPage(`${props.page}`);
      return;
    }

    // Clamp the value to ensure it stays within valid page bounds
    const clamped = Math.min(Math.max(parsed, 1), props.totalPages || 1);

    // Notify parent only when the page actually changes
    // This avoids unnecessary re-renders and update loops
    if (clamped !== props.page) {
      props.onPageChanged?.(clamped);
    }

    // Normalize the input value after committing
    setPage(`${clamped}`);
  };

  const keyListener: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.code === 'Enter') {
      // Commit the page change on Enter
      // and blur the input to keep behavior consistent with mouse interaction
      commitPage();
      inputRef.current?.blur();
    }
  };

  return {
    inputRef,
    page,
    onPageChanged,
    onBlur: commitPage,
    keyListener,
  };
};

export default usePresenter;